import { useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import { modalClasses } from '@mui/material/Modal';
import type { SidebarSide } from './useSidebarLayout';
import { otherModalOpen, sideOf, stranded, toggleFor } from './sheetFocus';

/**
 * Focus handed back out of a sheet can end up where nobody can use it, and then goes to that side's toggle.
 * A dialog opened from a sheet can outlive it (a turn across 900px closes the sheet underneath) and on close
 * hands focus back into the closed sheet. An opener focus went back to can vanish a moment later, as the empty
 * state's Open investigations does once the picked investigation loads. Polled at MUI's own trap interval,
 * since focus dropping to the body fires no event; the operator's own next pointer or key press ends the
 * watch, unless a modal is still showing.
 *
 * Returns the callback a sheet calls once it has handed focus back to its opener, which watches that opener.
 */
export function useStrandedFocusWatch(
  isCompact: boolean,
  openSheet: SidebarSide | null,
): (side: SidebarSide, opener: HTMLElement) => void {
  const live = useRef({ isCompact, openSheet });
  useLayoutEffect(() => {
    live.current = { isCompact, openSheet };
  });
  const watch = useRef<(side: SidebarSide, keep: Element | null) => void>(() => undefined);
  useEffect(() => {
    let side: SidebarSide | undefined;
    let keep: Element | null = null;
    let poll: ReturnType<typeof setInterval> | undefined;
    const stop = () => {
      side = undefined;
      keep = null;
      clearInterval(poll);
      poll = undefined;
    };
    const check = () => {
      if (otherModalOpen()) return;
      const active = document.activeElement;
      if (keep !== null && active === keep && keep.isConnected) return;
      if (!stranded(active)) {
        stop();
        return;
      }
      if (side && live.current.isCompact && live.current.openSheet === null) {
        toggleFor(side)?.focus();
        stop();
      }
    };
    const start = (from: SidebarSide, element: Element | null) => {
      side = from;
      keep = element;
      poll ??= setInterval(check, 50);
    };
    const onFocusIn = (event: FocusEvent) => {
      // From `md` up the side hosts are panels, which never close under a dialog.
      if (!live.current.isCompact) return;
      const { target, relatedTarget } = event;
      if (!(target instanceof Element) || !target.closest(`.${modalClasses.root}`)) return;
      const from = relatedTarget instanceof Element ? sideOf(relatedTarget) : undefined;
      if (from) start(from, null);
    };
    const onInput = () => {
      if (side && !otherModalOpen()) stop();
    };
    watch.current = start;
    document.addEventListener('focusin', onFocusIn, true);
    document.addEventListener('pointerdown', onInput, true);
    document.addEventListener('keydown', onInput, true);
    return () => {
      document.removeEventListener('focusin', onFocusIn, true);
      document.removeEventListener('pointerdown', onInput, true);
      document.removeEventListener('keydown', onInput, true);
      stop();
    };
  }, []);
  // Stable across renders, so a sheet's restore effect does not re-run for a new callback.
  return useCallback((side: SidebarSide, opener: HTMLElement) => watch.current(side, opener), []);
}
